import React, { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import GetTranslatedStrings from "@helpers/GetTranslatedStrings";
import classNames from "classnames";
import { FlexContainer } from "@components/ui/flex_container";
import CrossIcon from "./icons/CrossIcon";
import GoogleIcon from "./icons/GoogleIcon";
import Link from "next/link";
import useUser from "@hooks/use_user";
import { mutate } from "swr";
import { useSearchSuggestions } from "@hooks/1819";
import IndustryIcon from "./icons/IndustryIcon";
import PersonIcon from "./icons/PersonIcon";
import SearchIcon from "./icons/SearchIcon";
import { useClickOutside } from "@react-hooks-hub/use-click-outside";

const getRegistrationLink = (suggestion, query) => {
    // If no suggestion is provided, use the provided query (lowercased).
    if (!suggestion) {
        return new URL(`/?q=${(query || "").toLowerCase()}`, window.location.origin);
    }

    // If a suggestion exists but lacks a contact_id, use its result (lowercased).
    if (!suggestion.contact_id) {
        return new URL(`/?q=${(suggestion.result || "").toLowerCase()}`, window.location.origin);
    }

    // Determine the base URL: if suggestion.belongs_to exists, use that; otherwise, use contact_id.
    const basePath = suggestion.belongs_to
        ? `/info/${suggestion.belongs_to}`
        : `/info/${suggestion.contact_id}`;
    const openLink = new URL(basePath, window.location.origin);

    // If belongs_to exists, add the actual contact_id as a query parameter.
    if (suggestion.belongs_to) {
        openLink.searchParams.append("id", suggestion.contact_id);
    }

    // Append the "q" parameter using suggestion.result if available; otherwise, fallback to the provided query.
    const qParam = suggestion.result || query;
    if (qParam) {
        openLink.searchParams.append("q", qParam.toLowerCase());
    }

    return openLink;
};

const T1819SearchBar = React.forwardRef((props, ref) => {
    const {
        value,
        small,
        style,
        onSearch,
        maxWidth,
        placeholder,
        showGoogle,
        className,
        searchBarButtonStyle,
        onChange,
        searchSuggestionsEnabled = false,
        searchQuery: searchQueryProp,
        enableSearchResults = true,
        disableSuggestions = false,
    } = props;

    const user = useUser();
    const t = GetTranslatedStrings();
    const router = useRouter();
    const searchBar = useRef(null);
    const containerRef = useRef(null);
    const [searchQuery, setSearchQuery] = useState(
        searchQueryProp || (value ? value : router.query.q) || "",
    );
    const shouldEnableSearchResults = !disableSuggestions && enableSearchResults;
    const { data, error } = useSearchSuggestions(shouldEnableSearchResults ? searchQuery : null);

    const [selectedIndex, setSelectedIndex] = useState(null);

    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [searchSuggestionsVisible, setSearchSuggestionsVisible] = useState(false);

    useEffect(() => {
        if (data) {
            setSearchSuggestions(data);
        }
    }, [data]);

    useEffect(() => {
        if (router.query.q) {
            setSearchQuery(router.query.q);
        }
    }, [router.query.q]);

    useEffect(() => {
        if (searchQuery == "") {
            setSearchSuggestionsVisible(false);
        }
    }, [searchQuery]);

    const _handleKeyDown = (e) => {
        if (e.key === "Enter") {
            setSearchSuggestionsVisible(false);
            if (searchSuggestionsVisible && selectedIndex !== null) {
                let suggestion = searchSuggestions[selectedIndex];
                router.push(getRegistrationLink(suggestion, router.query.q));
            } else {
                _handleSearch();
            }
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            if (selectedIndex === null) {
                setSelectedIndex(0);
                return;
            }
            setSelectedIndex((selectedIndex + 1) % searchSuggestions.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (selectedIndex === null) {
                setSelectedIndex(searchSuggestions.length - 1);
                return;
            }
            setSelectedIndex(
                (selectedIndex - 1 + searchSuggestions.length) % searchSuggestions.length,
            );
        } else {
            if (!disableSuggestions) {
                setSearchSuggestionsVisible(true);
            }
        }
    };

    const _handleSearch = () => {
        if (onSearch) {
            onSearch(searchQuery);
            return;
        }
        if (!searchQuery || searchQuery?.length == 0) {
            return;
        }
        mutate(`1819/search?query=${searchQuery}`);
        router.push(`/?q=${searchQuery}`);
    };

    useEffect(() => {
        mutate(`1819/search?query=${searchQuery}`);
    }, [router]);

    const _handleClear = () => {
        setSearchQuery("");
        if (searchBar) {
            searchBar.current.focus();
        }
    };
    useEffect(() => {
        if (searchQueryProp !== undefined && searchQueryProp !== null) {
            setSearchQuery(searchQueryProp);
            if (searchBar.current) {
                searchBar.current.focus();
            }
        }
    }, [searchQueryProp]);

    useClickOutside([containerRef], (isOutside) => {
        setSearchSuggestionsVisible(isOutside);
    });

    return (
        <>
            <FlexContainer
                ref={containerRef}
                className={classNames("search-bar-container", className)}
                style={{
                    maxWidth: maxWidth && !small ? maxWidth : null,
                }}
            >
                <input
                    ref={searchBar}
                    autoFocus={!small}
                    type="text"
                    placeholder={placeholder || t.searchPlaceholder}
                    value={searchQuery}
                    style={{
                        ...style,
                    }}
                    onKeyDown={_handleKeyDown}
                    onChange={(event) => {
                        setSearchQuery(event.target.value);
                        onChange && onChange(event.target.value);
                    }}
                    className={classNames(
                        "torgid-background-secondary torgid-border search-bar-input",
                        { small: small },
                    )}
                />

                <FlexContainer column className="search-suggestions-container">
                    {!disableSuggestions &&
                        searchSuggestionsVisible &&
                        searchSuggestions?.map((suggestion, index) => {
                            let icon = null;
                            if (suggestion.logo_link) {
                                icon = (
                                    <img
                                        src={suggestion.logo_link}
                                        style={{
                                            maxHeight: 30,
                                            maxWidth: 32,
                                            aspectRatio: 1,
                                            objectFit: "contain",
                                        }}
                                    />
                                );
                            } else if (suggestion.is_type == 2) {
                                // Company
                                icon = (
                                    <IndustryIcon
                                        style={{
                                            color: "gray",
                                            maxHeight: 30,
                                            maxWidth: 32,
                                            aspectRatio: 1,
                                            objectFit: "contain",
                                        }}
                                    />
                                );
                            } else if (suggestion.is_type == 1) {
                                // PERSON
                                icon = (
                                    <PersonIcon
                                        src={suggestion.logo_link}
                                        style={{
                                            maxHeight: 30,
                                            maxWidth: 32,
                                            aspectRatio: 1,
                                            objectFit: "contain",
                                            color: "gray",
                                        }}
                                    />
                                );
                            } else {
                                // Keyword
                                icon = (
                                    <SearchIcon
                                        src={suggestion.logo_link}
                                        style={{
                                            maxHeight: 23,
                                            maxWidth: 32,
                                            aspectRatio: 1,
                                            objectFit: "contain",
                                            color: "gray",
                                        }}
                                    />
                                );
                            }

                            return (
                                <Link href={getRegistrationLink(suggestion, router.query.q)}>
                                    <FlexContainer
                                        onClick={() => {
                                            setSearchSuggestionsVisible(false);
                                        }}
                                        key={index}
                                        className={classNames("search-suggestion", {
                                            selected: selectedIndex === index,
                                        })}
                                    >
                                        <div
                                            style={{
                                                aspectRatio: 1,
                                                height: 30,
                                                width: 32,
                                                marginLeft: 10,
                                                marginRight: 10,
                                            }}
                                        >
                                            {icon}
                                        </div>
                                        {suggestion.result}
                                    </FlexContainer>
                                </Link>
                            );
                        })}
                </FlexContainer>

                <FlexContainer
                    style={{
                        position: "absolute",
                        flex: 1,
                        flexDirection: "row",
                        right: "10px",
                        padding: 0,
                        margin: 0,
                    }}
                    justifyContent="center"
                    alignItems="center"
                >
                    {user && user?.user_type != "normal" && showGoogle && (
                        <Link
                            href={
                                searchQuery.length == 0
                                    ? "#"
                                    : `https://www.google.com/search?q=${searchQuery}`
                            }
                        >
                            <a
                                target={searchQuery.length == 0 ? "" : "_blank"}
                                style={{ marginRight: small ? 0 : 5 }}
                            >
                                <div
                                    style={{
                                        backgroundColor: "#fbbc05",
                                        borderRadius: 100,
                                        flexGrow: 1,
                                    }}
                                >
                                    <GoogleIcon
                                        height={small ? 25 : 35}
                                        width={small ? 40 : 60}
                                        style={{
                                            marginBottom: "1px",
                                            justifySelf: "center",
                                            cursor: "pointer",
                                        }}
                                    />
                                </div>
                            </a>
                        </Link>
                    )}

                    {small && searchQuery?.length != 0 && (
                        <>
                            <CrossIcon
                                height="15px"
                                style={{
                                    paddingRight: "5px",
                                    paddingLeft: "5px",
                                    justifySelf: "center",
                                    cursor: "pointer",
                                }}
                                onClick={_handleClear}
                            />
                        </>
                    )}

                    <button
                        type="submit"
                        className={classNames("search-bar-button", {
                            small: small,
                        })}
                        style={{ ...searchBarButtonStyle }}
                        onClick={_handleSearch}
                    >
                        {t.searchButton}
                    </button>
                </FlexContainer>
            </FlexContainer>
            {!small && (
                <button
                    className={classNames("search-bar-button-below", {
                        small: small,
                    })}
                    type="submit"
                    onClick={_handleSearch}
                >
                    {t.searchButton}
                </button>
            )}
        </>
    );
});

T1819SearchBar.displayName = "T1819SearchBar"; // ✅ Add this line

export default T1819SearchBar;
